import React, { useState } from 'react';
import { Modal, Input, Button, Form, message } from 'antd';
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { Formik, Field, Form as FormikForm, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { updatePassword } from '../../../core/data/redux/user/userAction'; // Adjust this import path as necessary
import { AppDispatch } from '../../../core/data/redux/store';

// Type for the visibility of the password fields
interface PasswordVisibility {
    currentPassword: boolean;
    newPassword: boolean;
    confirmPassword: boolean;
}

// Props for the ChangePasswordModal
interface ChangePasswordModalProps {
    visible: boolean;
    onClose: () => void;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ visible, onClose }) => {
    const dispatch = useDispatch<AppDispatch>();
    const { user } = useSelector((state: any) => state.auth); // Assuming your state is auth
    const { passwordUpdateStatus } = useSelector((state: any) => state.user); // Assuming your state is auth
    const [passwordVisibility, setPasswordVisibility] = useState<PasswordVisibility>({
        currentPassword: false,
        newPassword: false,
        confirmPassword: false,
    });

    const togglePasswordVisibility = (field: keyof PasswordVisibility) => {
        setPasswordVisibility((prevState) => ({
            ...prevState,
            [field]: !prevState[field],
        }));
    };

    const handleSubmit = (values: { currentPassword: string; newPassword: string; confirmPassword: string },{ resetForm }: any) => {
        // Call the updatePassword action (ensure the arguments match your API)
        dispatch(updatePassword({
            userId: user?.UserId,
            oldPassword: values.currentPassword,
            newPassword: values.newPassword,
        }))
            .then((response: any) => {   
                if (response.meta.requestStatus === "rejected") {
                    message.error(response.payload.error || 'An unknown error occurred.');
                } else if (response.meta.requestStatus === "fulfilled") {
                    message.success(response.payload.message);
                    resetForm(); 
                    onClose();
                }
            })
            .catch((error) => {
                console.error("Error occurred: ", error); 
                message.error('Password update failed. Please try again.');
            });
    };
   


    // Form validation schema using Yup
    const validationSchema = Yup.object({
        currentPassword: Yup.string()
            .min(6, 'Password must be at least 6 characters')
            .required('Current password is required'),
        newPassword: Yup.string()
            .min(6, 'Password must be at least 6 characters')
            .required('New password is required'),
        confirmPassword: Yup.string()
            .oneOf([Yup.ref('newPassword')], 'Passwords must match')
            .required('Confirm password is required'),
    });

    return (
        <Modal
            visible={visible}
            title={<span style={{ color: 'red' }}>Change Password</span>}
            onCancel={onClose}
            footer={null}
            centered
            width={350} // Increased width for better visibility
            bodyStyle={{ padding: '0 4px 4px' }} // Added padding for the modal body
        >
            <Formik
                initialValues={{ currentPassword: '', newPassword: '', confirmPassword: '' }}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {({ isSubmitting ,resetForm}) => (
                    <FormikForm>
                        <Field name="currentPassword">
                            {({ field }: any) => (
                                <Form.Item
                                    label="Current Password"
                                    style={{ marginBottom: '10px' }} // Consistent margin between fields
                                >
                                    <Input.Password
                                        {...field}
                                        type={passwordVisibility.currentPassword ? 'text' : 'password'}
                                        placeholder="Enter your current password"
                                        iconRender={(visible) =>
                                            visible ? <EyeOutlined style={{ color: '#1890ff' }} /> : <EyeInvisibleOutlined style={{ color: '#1890ff' }} />
                                        }
                                        onClick={() => togglePasswordVisibility('currentPassword')}
                                        style={{ borderRadius: '8px' }} // Rounded corners for the input
                                    />
                                    <div style={{ color: 'red', fontSize: '12px' }}>
                                        <ErrorMessage name="currentPassword" />
                                    </div>
                                </Form.Item>
                            )}
                        </Field>

                        <Field name="newPassword">
                            {({ field }: any) => (
                                <Form.Item
                                    label="New Password"
                                    style={{ marginBottom: '10px' }} // Uniform spacing
                                >
                                    <Input.Password
                                        {...field}
                                        type={passwordVisibility.newPassword ? 'text' : 'password'}
                                        placeholder="Enter a new password"
                                        iconRender={(visible) =>
                                            visible ? <EyeOutlined style={{ color: '#1890ff' }} /> : <EyeInvisibleOutlined style={{ color: '#1890ff' }} />
                                        }
                                        onClick={() => togglePasswordVisibility('newPassword')}
                                        style={{ borderRadius: '8px' }} // Consistent rounded borders
                                    />
                                    <div style={{ color: 'red', fontSize: '12px' }}>
                                        <ErrorMessage name="newPassword" />
                                    </div>
                                </Form.Item>
                            )}
                        </Field>

                        <Field name="confirmPassword">
                            {({ field }: any) => (
                                <Form.Item
                                    label="Confirm Password"
                                    style={{ marginBottom: '20px' }} // Even spacing for all fields
                                >
                                    <Input.Password
                                        {...field}
                                        type={passwordVisibility.confirmPassword ? 'text' : 'password'}
                                        placeholder="Confirm your new password"
                                        iconRender={(visible) =>
                                            visible ? <EyeOutlined style={{ color: '#1890ff' }} /> : <EyeInvisibleOutlined style={{ color: '#1890ff' }} />
                                        }
                                        onClick={() => togglePasswordVisibility('confirmPassword')}
                                        style={{ borderRadius: '8px' }} // Round the corners
                                    />
                                    <div style={{ color: 'red', fontSize: '12px' }}>
                                        <ErrorMessage name="confirmPassword" />
                                    </div>
                                </Form.Item>
                            )}
                        </Field>

                        <Form.Item>
                            <Button
                                type="primary"
                                block
                                htmlType="submit"
                                style={{ borderRadius: '8px', padding: '4px' }}
                            >
                                Change Password
                            </Button>
                        </Form.Item>
                    </FormikForm>
                )}
            </Formik>
        </Modal>
    );
};

export default ChangePasswordModal;
